import React, { useEffect, useState } from 'react';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ls from "@livesession/browser";
import '../styles/design-system.css';
import '../styles/global-style-override.css';
import '../styles/mobile-spacing-fix.css';
import '../styles/no-underlines.css';
import '../styles/Dashboard.css';
// Import for preloading listening challenges
import { getListeningGroups } from '../api/contests';

// Components
import Sidebar from '../components/layout/Sidebar';
import AdBlockDetector from '../components/AdBlockDetector';
import Saved from '../components/Saved';
import { SessionCreationProvider } from '../contexts/SessionCreationContext';
import { 
  ListeningChallenges, 
  ListeningInstructions, 
  ListeningChallenge 
} from '../pages/contests';

// Pages
import DashboardView from '../pages/dashboard/DashboardView';
import TrainingView from '../pages/training/TrainingView';
import PracticeMode from '../pages/training/PracticeMode';
import DiagnosticStats from '../pages/training/DiagnosticStats';
import SessionStats from '../pages/training/SessionStats';
import DiagnosticExam from '../pages/training/DiagnosticExam';
import PersonalizedExam from '../pages/training/PersonalizedExam';
import ReviewPersonalizedExam from '../pages/training/ReviewPersonalized';
// import ReviewQuestion from '../pages/training/ReviewQuestion';
import ModernReviewQuestion from '../pages/training/ModernReviewQuestion';
import Stats from '../pages/training/Stats';
import MockExam from '../pages/training/MockExam';
import MockExamResults from '../pages/training/MockExamResults';
import MockExamReview from '../pages/training/MockExamReview';

import Exam from '../pages/training/Exam';
import ReviewExam from '../pages/training/ReviewExam';
import ReviewExamQuestion from '../pages/training/ReviewExamQuestion';

import AskMina from '../features/chat/AskMina';

import Login from '../pages/auth/Login';
import Signup from '../pages/onboarding/SignUp';
import EmailVerification from '../pages/onboarding/EmailVerification';
import ForgotPassword from '../pages/onboarding/forgotpassword';
import ResetPassword from '../pages/onboarding/resetpassword';
import JLPTExperienceStep from '../pages/onboarding/JLPTExperienceStep';
import TargetLevelStep from '../pages/onboarding/TargetLevelStep';
import LevelTestIntro from '../pages/onboarding/LevelTestIntro';

// Admin Dashboard
import DiagnosticExams from '../pages/dashboard/DiagnosticExams';

// Settings
import SettingsView from '../pages/settings/SettingsView';

// Utilities and other
import '../styles/App.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import MyCheckoutComponent from '../pages/payments/MyCheckoutComponent';
import PaymentSuccess from '../pages/payments/PaymentSuccess';
import PaymentCanceled from '../pages/payments/PaymentCanceled';
import { STRIPE_PUBLIC_KEY, API_URL } from './constants'

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Custom component to handle route changes
function RouteChangeHandler({ onRouteChange }) {
  const location = useLocation();
  
  React.useEffect(() => {
    onRouteChange();
  }, [location, onRouteChange]);
  
  return null; // This component doesn't render anything
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  
  // Initialize LiveSession
  useEffect(() => {
    ls.init("3d0a16c7.b9831172");
    ls.newPageView();
  }, []);
  
  // Add body classes for routes where we want to hide the menu toggle button
  useEffect(() => {
    const handleRouteChange = () => {
      const pathname = window.location.pathname;
      
      // Remove all special route classes first
      document.body.classList.remove('personalized-exam', 'diagnostic-exam', 'contests', 'instructions', 'challenge', 'review-question');
      
      // Add appropriate class based on current route
      if (pathname.includes('/review-question/')) {
        document.body.classList.add('review-question');
      } else if (pathname.includes('/personalized-exam/')) {
        document.body.classList.add('personalized-exam');
      } else if (pathname.includes('/diagnostic-exam/')) {
        document.body.classList.add('diagnostic-exam');
      } else if (pathname.includes('/contests')) {
        document.body.classList.add('contests');
        
        // Add more specific classes for different contest pages
        if (pathname.includes('/contests/instructions/')) {
          document.body.classList.add('instructions');
        } else if (pathname.includes('/contests/challenge/') || pathname.includes('/contests/user-set/')) {
          document.body.classList.add('challenge');
        }
      }
    };
    
    // Check on initial load
    handleRouteChange();
    
    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
  
  // Preload listening challenges data
  useEffect(() => {
    if (isAuthenticated) {
      // Start preloading contest data in the background as soon as authenticated
      setTimeout(() => {
        console.log("Preloading listening challenges data in the background");
        getListeningGroups().catch(err => 
          console.error("Error preloading listening challenges:", err)
        );
      }, 2000); // Delay by 2 seconds to let more important resources load first
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // First check if trying to access admin routes directly
    const currentPath = window.location.pathname;
    const adminRoutes = [
      '/DiagnosticExams', 
      '/direct-admin-access',
      '/admin-stats-7c39a',
      '/admin-access-mina',
      '/teacher-panel-access',
      '/diagnostic-dashboard',
      '/account-info',
      '/user-profile'
    ];
    
    // If direct admin access, set flag to bypass authentication
    if (adminRoutes.some(route => currentPath === route)) {
      console.log('Direct admin access detected:', currentPath);
      setIsAuthenticated(true);
      return; // Skip other checks
    }
    
    // Check current URL to detect onboarding routes
    const onboardingRoutes = ['/jlpt-experience', '/target-level', '/level-test', '/email-verification'];
    const isOnboardingRoute = onboardingRoutes.some(route => currentPath.startsWith(route));
    
    setIsOnboarding(isOnboardingRoute);
    
    // Check for JWT in local storage
    const jwt = localStorage.getItem('jwt');
    
    if (jwt) {
      const checkUserStatus = async () => {
        try {
          const response = await fetch(`${API_URL}/api/v1/auth/me`, {
            headers: { 'Authorization': `Bearer ${jwt}` },
            method: "GET",
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUserStatus(userData.status);
            
            // Check if email is verified - redirect to verification if not
            if (!userData.email_verified && !isOnboardingRoute) {
              window.location.href = '/email-verification';
              return;
            }
          }
        } catch (error) {
          console.error("Error checking user status:", error);
        }
      };
      
      setIsAuthenticated(true);
      checkUserStatus();
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('jwt');
    setIsAuthenticated(false);
    window.location.reload();
  };
  
  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };


  // When user is in onboarding flow or not authenticated
  if (!isAuthenticated || isOnboarding) {
    return (
      <SessionCreationProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/register" element={<Signup />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/jlpt-experience" element={<JLPTExperienceStep />} />
            <Route path="/target-level" element={<TargetLevelStep />} />
            <Route path="/level-test" element={<LevelTestIntro />} />
            {/* Add diagnostic exam route for onboarding flow */}
            <Route path="/diagnostic-exam/:exam_id" element={<DiagnosticExam />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </SessionCreationProvider>
    );
  }

  // Update route classes when navigating with React Router
  const handleRouteUpdate = () => {
    const pathname = window.location.pathname;
    
    // Remove all special route classes first
    document.body.classList.remove('personalized-exam', 'diagnostic-exam', 'contests', 'instructions', 'challenge');
    
    // Add appropriate class based on current route
    if (pathname.includes('/personalized-exam/')) {
      document.body.classList.add('personalized-exam');
    } else if (pathname.includes('/diagnostic-exam/')) {
      document.body.classList.add('diagnostic-exam');
    } else if (pathname.includes('/contests')) {
      document.body.classList.add('contests');
      
      // Add more specific classes for different contest pages
      if (pathname.includes('/contests/instructions/')) {
        document.body.classList.add('instructions');
      } else if (pathname.includes('/contests/challenge/') || pathname.includes('/contests/user-set/')) {
        document.body.classList.add('challenge');
      }
    }
  };

  return (
    <SessionCreationProvider>
      <Router>
        <div className="App">
          {/* Custom route change handler */}
          <RouteChangeHandler onRouteChange={handleRouteUpdate} />
          
          <Sidebar 
            isMobileMenuOpen={isMobileMenuOpen}
            onMobileMenuClose={handleMobileMenuClose}
            onMobileMenuToggle={toggleMobileMenu}
            onLogout={handleLogout}
            onGetFullAccess={() => window.location.href = 'https://buy.stripe.com/14A28q2FTaLN3zwbSsenS00'}
            userStatus={userStatus}
          />
          
          <AdBlockDetector />
        
        <div className="main-content">
          <Routes>
            {/* Main routes - match exactly what's in the sidebar navItems */}
            <Route path="/" element={<DashboardView />} />
            <Route path="/study_plan" element={<TrainingView />} />
            <Route path="/practice" element={<PracticeMode />} />
            <Route path="/saved" element={<Saved />} />
            <Route path="/settings" element={<SettingsView />} />
            <Route path="/mock-exam/:level" element={<MockExam />} />
            <Route path="/mock-exam-results" element={<MockExamResults />} />
            <Route path="/mock-exam-review" element={<MockExamReview />} />
            
            {/* Hidden admin portal with innocent name */}

            {/* Legacy routes redirect to main training */}
            <Route path="/study_plan/:id" element={<TrainingView />} />
            <Route path="/selected_study" element={<TrainingView />} />
            <Route path="/dashboard" element={<DashboardView />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/diagnostic-stats" element={
              <>
                <style dangerouslySetInnerHTML={{__html: `
                  /* Hide sidebar and make page full screen for diagnostic-stats */
                  .sidebar {
                    display: none !important;
                  }
                  .main-content {
                    margin-left: 0 !important;
                    width: 100% !important;
                  }
                  .menu-toggle {
                    display: none !important;
                  }
                `}} />
                <DiagnosticStats />
              </>
            } />
            <Route path="/session-stats/:session_id" element={
              <>
                <style dangerouslySetInnerHTML={{__html: `
                  /* Hide sidebar and make page full screen for session-stats */
                  .sidebar {
                    display: none !important;
                  }
                  .main-content {
                    margin-left: 0 !important;
                    width: 100% !important;
                  }
                  .menu-toggle {
                    display: none !important;
                  }
                `}} />
                <SessionStats />
              </>
            } />
            <Route path="/exam/:exam_id" element={<Exam />} />
            <Route path="/personalized-exam/:exam_id" element={<PersonalizedExam />} />
            <Route path="/diagnostic-exam/:exam_id" element={<DiagnosticExam />} />
            <Route path="/askmina" element={<AskMina />} />
            <Route path="/review-exam/:exam_id" element={<ReviewExam />} />
            <Route path="/review-personalized/:exam_id" element={<ReviewPersonalizedExam />} />
            <Route path="/review-question/:exam_id" element={
              <>
                <style dangerouslySetInnerHTML={{__html: `
                  /* Hide menu toggle for this specific route */
                  .menu-toggle {
                    display: none !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                  }
                `}} />
                <ModernReviewQuestion />
              </>
            } />
            <Route path="/review-exam-question/:exam_id" element={<ReviewExamQuestion />} />
            
            {/* Admin routes */}
            <Route path="/DiagnosticExams" element={<DiagnosticExams />} />
            <Route path="/admin-stats-7c39a" element={<DiagnosticExams />} />
            
            {/* Other routes */}
            <Route path="/checkout" element={
              <Elements stripe={stripePromise}>
                <MyCheckoutComponent />
              </Elements>
            } />
            <Route path="/payment/succeed" element={<PaymentSuccess />} />
            <Route path="/payment/canceled" element={<PaymentCanceled />} />
            
            {/* Admin dashboard routes with secret names */}
            
            {/* Contests routes - split into three separate routes */}
            <Route path="/contests" element={<ListeningChallenges />} />
            <Route path="/contests/instructions/:groupId" element={<ListeningInstructions />} />
            <Route path="/contests/challenge/:groupId/:challengeIndex" element={<ListeningChallenge />} />
            <Route path="/contests/user-set/:setId/:challengeIndex" element={<ListeningChallenge />} />
            
            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </Router>
  </SessionCreationProvider>
  );
}

export default App;